// Qué modo va en cada sección — UNA lista para la home (los popups de "Redes sociales" y
// "Catálogos") y la sidebar (sus paneles de "Redes sociales", "Catálogo" y
// "Herramientas"), ADR-2026-10-05-la-sidebar-por-secciones.
//
// Cada modo vive en UNA sola sección. Los que dependen del rubro van todos en la lista y
// los filtra quien la dibuja con los flags del catálogo (un rubro ve uno u otro, nunca los
// dos: `plantillas` / `plantillas-belleza`, `catalogo` / `catalogo-muebles`).
//
// Fuera a propósito: Prompt libre, Creador de video y Automática (Crear hace lo de los dos
// primeros en una pantalla; ADR-2026-10-05-la-sidebar-por-secciones) y Anuncio
// (ADR-2026-10-05-anuncio-sale-de-toda-la-app). En el celular no hay sidebar y sus listas
// no salen de acá.

import { esRubroNoPrincipal, RUBRO_BELLEZA, RUBRO_HOGAR_DECO } from '../../lib/rubro';
import { RUBRO_GASTRONOMIA } from '../v2/modes/redes/contenidoPorRubro';

/** Crear y Recrear, en ese orden. */
export const MODOS_DE_REDES: readonly string[] = ['redes', 'plantillas', 'plantillas-belleza'];

/**
 * Lo que ocupa el lugar de Recrear en un rubro SIN plantillas (todos menos Moda e
 * Indumentaria y Belleza y Cuidado Personal): Replicar imagen, que copia una foto que trae el
 * usuario en vez de una plantilla del catálogo
 * (ADR-2026-10-12-replicar-imagen-en-lugar-de-recrear).
 */
export const RECREAR_SIN_PLANTILLAS = 'replicar-foto';

/**
 * Los modos de "Redes sociales" que ve ESTE rubro, en orden, según cuáles tiene (`tiene`):
 * Crear y Recrear desde una plantilla; sin plantillas, Replicar imagen en el lugar de
 * Recrear. Lo usan el popup de la home, el panel de la sidebar y el sheet del teléfono.
 */
export function modosDeRedesDelRubro(tiene: (path: string) => boolean): string[] {
  const propios = MODOS_DE_REDES.filter(tiene);
  const conPlantillas = propios.some((p) => p !== 'redes');
  return conPlantillas || !tiene(RECREAR_SIN_PLANTILLAS) ? propios : [...propios, RECREAR_SIN_PLANTILLAS];
}

/** Lo de la tienda, en el orden en que se muestran. */
export const MODOS_DE_CATALOGO: readonly string[] = [
  'modelo',
  'fotos-producto',
  'catalogo',
  'catalogo-agente',
  'infografia',
  'mostrar-como-funciona-belleza',
  // Para todos (ADR-2026-10-31-videos-cortos-para-todos).
  'videos-cortos',
];

/**
 * Los modos de "Catálogos" que ve ESTE rubro. Los rubros no principales (Gastronomía, Otros…)
 * ven sólo el Catálogo con el agente, y después "Videos cortos"
 * (ADR-2026-10-26-catalogo-con-el-agente-para-los-rubros-no-principales). El resto, la lista
 * entera (cada uno ve lo suyo por los flags del catálogo). Lo usan el popup de la home, el
 * panel de la sidebar y el sheet del teléfono.
 */
export function modosDeCatalogoDelRubro(rubro?: string | null): readonly string[] {
  return esRubroNoPrincipal(rubro) ? ['catalogo-agente', 'videos-cortos'] : MODOS_DE_CATALOGO;
}

/** Lo que todavía no existe y se anuncia como "Próximamente" al final del catálogo. */
export const PROXIMOS_DE_CATALOGO: readonly { nombre: string; bajada: string; path: string }[] = [
  // Vacío: Videos cortos salió para todos (ADR-2026-10-31-videos-cortos-para-todos). El mecanismo queda para el
  // próximo anuncio.
];

/**
 * Los "Próximamente" que quedan para esta persona: uno se va cuando su modo de verdad ya está
 * en la lista (un dev tester ve "Videos cortos" y no su anuncio;
 * ADR-2026-10-29-videos-cortos-visible-para-dev-testers). `enLaLista` = los paths que se dibujan.
 */
export function proximosQueQuedan<T extends { path?: string }>(proximos: readonly T[], enLaLista: readonly string[]): T[] {
  return proximos.filter((p) => !p.path || !enLaLista.includes(p.path));
}

/**
 * Los MODOS de Herramientas. El panel abre con las ediciones rápidas
 * (`EDICIONES_RAPIDAS`, que no son modos sino atajos) y sigue con éstos. Carrusel y Animar
 * no van acá: ya están entre las ediciones rápidas.
 */
export const MODOS_DE_HERRAMIENTAS: readonly string[] = [
  'replicar-foto',
  'editor-video',
  'diseno-indumentaria',
  'flujos',
];

/**
 * Las carátulas con el fondo del tinte de su sección en vez del lavanda de siempre
 * (ADR-2026-10-05-caratulas-con-el-tinte-de-su-seccion): las usan los popups de la home y
 * los paneles de la sidebar. Son las mismas carátulas recoloreadas con
 * `scripts/caratulas-con-tinte/recolorear.py`. Una portada del rubro gana (es otra foto).
 */
export const CARATULAS_CON_TINTE: Readonly<Record<'redes' | 'catalogo', Readonly<Record<string, string>>>> = {
  redes: {
    // "Imagen automática" del popup de Redes (ADR-2026-10-12-imagen-automatica-en-el-popup-de-redes).
    // La versión de moda (prendas) que subió Maxi, pasada al tinte de la sección.
    auto: '/images/modo_automatico/caratula_moda_violeta.jpg',
    // Crear todavía usa la de Prompt libre (prestada, ver `MODES`).
    redes: '/images/modo_prompt_libre/caratula_violeta.jpg',
    plantillas: '/images/modo_plantillas/caratula_violeta.jpg',
    // Belleza usa por ahora la misma carátula que las de Moda.
    'plantillas-belleza': '/images/modo_plantillas/caratula_violeta.jpg',
  },
  catalogo: {
    modelo: '/images/modo_con_modelo/caratula_amarilla.jpg',
    'fotos-producto': '/images/modo_fotos_producto/caratula_amarilla.jpg',
    catalogo: '/images/modo_catalogo_ia/caratula_amarilla.jpg',
    // Videos cortos: su carátula ya es crema, el tinte de la sección (ADR-2026-10-31-videos-cortos-para-todos).
    'videos-cortos': '/images/modo_videos_cortos/caratula.jpg',
    // El Catálogo con el agente en los rubros no principales: la misma que Generación en lote
    // (ADR-2026-10-26-catalogo-con-el-agente-para-los-rubros-no-principales). Hogar tiene la
    // suya, con muebles, abajo.
    'catalogo-agente': '/images/modo_catalogo_ia/caratula_amarilla.jpg',
  },
};

/**
 * Excepciones por rubro a `CARATULAS_CON_TINTE`: la misma sección y el mismo tinte, con otra
 * foto. Belleza ve las tres cards de Redes con productos de belleza en vez de ropa
 * (ADR-2026-10-12-imagen-automatica-en-el-popup-de-redes-5 y -6).
 */
const CARATULAS_CON_TINTE_POR_RUBRO: Readonly<
  Record<string, Partial<Record<'redes' | 'catalogo', Readonly<Record<string, string>>>>>
> = {
  [RUBRO_BELLEZA]: {
    redes: {
      auto: '/images/modo_automatico/caratula_violeta.jpg',
      redes: '/images/modo_prompt_libre/caratula_belleza_violeta.jpg',
      // Belleza ve `plantillas-belleza`; `plantillas` por si el rubro llega con el de Moda.
      'plantillas-belleza': '/images/modo_plantillas/caratula_belleza_violeta.jpg',
      plantillas: '/images/modo_plantillas/caratula_belleza_violeta.jpg',
    },
    // Las de Catálogos con productos de belleza (ADR-2026-10-12-catalogo-de-belleza).
    catalogo: {
      modelo: '/images/modo_con_modelo/caratula_belleza_amarilla.jpg',
      'fotos-producto': '/images/modo_fotos_producto/caratula_belleza_amarilla.jpg',
      catalogo: '/images/modo_catalogo_ia/caratula_belleza_amarilla.jpg',
    },
  },
  // Hogar y Decoración: las tres cards de Redes con muebles, las que subió Maxi pasadas al
  // tinte de la sección (ADR-2026-10-12-redes-de-hogar). Ganan a la portada del rubro de
  // `auto` (la silla rosa), que sigue en las listas sin tinte.
  [RUBRO_HOGAR_DECO]: {
    redes: {
      auto: '/images/modo_automatico/caratula_hogar_violeta.jpg',
      'replicar-foto': '/images/modo_replicar_foto/caratula_hogar_violeta.jpg',
      redes: '/images/modo_prompt_libre/caratula_hogar_violeta.jpg',
    },
    // Su carátula de siempre, los muebles en abanico sobre crema
    // (ADR-2026-10-15-la-caratula-del-catalogo-con-el-agente).
    catalogo: {
      'catalogo-agente': '/images/modo_catalogo_agente/caratula-crema.jpg',
    },
  },
  // Gastronomía: las tres cards de Redes con comida, las que subió Maxi pasadas al tinte de la
  // sección (ADR-2026-10-12-redes-de-gastronomia y -2).
  [RUBRO_GASTRONOMIA]: {
    redes: {
      auto: '/images/modo_automatico/caratula_gastronomia_violeta.jpg',
      'replicar-foto': '/images/modo_replicar_foto/caratula_gastronomia_violeta.jpg',
      redes: '/images/modo_prompt_libre/caratula_gastronomia_violeta.jpg',
    },
    // Las de Catálogos con comida, al tinte de la sección (ADR-2026-10-12-catalogo-de-gastronomia).
    catalogo: {
      modelo: '/images/modo_con_modelo/caratula_gastronomia_amarilla.jpg',
      'fotos-producto': '/images/modo_fotos_producto/caratula_gastronomia_amarilla.jpg',
      catalogo: '/images/modo_catalogo_ia/caratula_gastronomia_amarilla.jpg',
      'catalogo-agente': '/images/modo_catalogo_ia/caratula_gastronomia_amarilla.jpg',
    },
  },
};

/**
 * ¿El rubro tiene carátula con tinte PROPIA para ese modo en esa sección? Si la tiene, gana a
 * la portada del rubro (`portadaDeRubro`), que es una foto pensada para las listas sin tinte
 * (ADR-2026-10-12-redes-de-hogar).
 */
export const tieneCaratulaDelRubro = (
  seccion: 'redes' | 'catalogo',
  path: string,
  rubro?: string | null,
): boolean => !!(rubro && CARATULAS_CON_TINTE_POR_RUBRO[rubro]?.[seccion]?.[path]);

/** La carátula con tinte de un modo en su sección, con la excepción del rubro si la hay. */
export const caratulaConTinte = (
  seccion: 'redes' | 'catalogo',
  path: string,
  rubro?: string | null,
): string | undefined =>
  (rubro ? CARATULAS_CON_TINTE_POR_RUBRO[rubro]?.[seccion]?.[path] : undefined) ??
  CARATULAS_CON_TINTE[seccion][path];

/**
 * El modo PRINCIPAL de una sección, el que los menús por destino destacan con el doble de ancho
 * (ADR-2026-10-31-el-modo-principal-de-cada-seccion): en Redes, Crear desde cero cuando hay plantillas (Recrear
 * desde una plantilla compite con él y Crear es la puerta de entrada); en Catálogos, el catálogo: el Catálogo con el
 * asistente donde está (Hogar y Decoración) y si no Generación en lote (Moda, Belleza). Con menos de tres modos no
 * destaca ninguno: no quedan "otros" para poner al costado. Puro. `paths` = los modos que dibuja la sección.
 */
export function modoPrincipalDeLaSeccion(seccion: 'redes' | 'catalogo', paths: readonly string[]): string | null {
  if (paths.length < 3) return null;
  if (seccion === 'redes') {
    const conPlantillas = paths.includes('plantillas') || paths.includes('plantillas-belleza');
    return conPlantillas && paths.includes('redes') ? 'redes' : null;
  }
  if (paths.includes('catalogo-agente')) return 'catalogo-agente';
  return paths.includes('catalogo') ? 'catalogo' : null;
}

/** Las columnas del popup por destino según cuántas tarjetas tiene: una sola ocupa todo el
 *  ancho y dos van de a dos, sin hueco al lado (#184); con tres o más, dos y en la compu tres
 *  (ADR-2026-10-15-los-popups-por-destino-en-tres-columnas). */
export function columnasDelPopup(tarjetas: number, conPrincipal = false): string {
  if (tarjetas <= 1) return 'grid-cols-1';
  if (tarjetas === 2) return 'grid-cols-2';
  // Con un principal (dos columnas por dos filas) y tres o más chicos, cuatro columnas: los chicos llenan el bloque
  // de 2×2 a su costado en vez de dejar uno solo en la fila de abajo
  // (ADR-2026-10-31-el-modo-principal-de-cada-seccion-2).
  if (conPrincipal && tarjetas - 1 >= 3) return 'grid-cols-2 lg:grid-cols-4';
  return 'grid-cols-2 lg:grid-cols-3';
}

/**
 * El orden de los chicos al costado del principal en una grilla de cuatro columnas: con tres, el último pasa al lado
 * del primero (arriba) y el segundo queda abajo, que es donde Matías quiere el que sobra
 * (ADR-2026-10-31-el-modo-principal-de-cada-seccion-2). Con otra cantidad, el orden de siempre. Puro.
 */
export function ordenAlCostadoDelPrincipal<T>(chicos: readonly T[]): T[] {
  return chicos.length === 3 ? [chicos[0]!, chicos[2]!, chicos[1]!] : [...chicos];
}
